import { useState, useEffect, useContext, useRef } from 'react';
import {
    Row,
    Col,
    Button,
    Modal,
    ModalBody,
    FormGroup,
    Label,
} from 'reactstrap';
import {
    TextInput,
    CheckboxInput,
    SelectInput,
    stateOptions,
} from '~/shared/components/form/Inputs';
import { useForm } from 'react-hook-form';
import { PaymentOptions } from '~/enums/PaymentOptions.enum';
import {
    createchangerequest,
    achDocumentPresignedUrlByCampaign,
} from '~/services/campaign';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FundRequestContext } from '~/context/fundrequests';
import { uploadFile, resizeImage } from '~/services/fundrequests';
import { CampaignContext } from '~/context/campaign';
import { normalizeFileName } from '~/shared/utils';

const paymentOptionsArray = [
    { label: 'Check', value: PaymentOptions.CHECK },
    { label: 'Direct Deposit (ACH)', value: PaymentOptions.ACH },
];

// Match values with AchInfoForm.tsx
const achAccountTypeOptions = [
    { label: 'Checking', value: 'CHECKING' },
    { label: 'Savings', value: 'SAVINGS' },
];

// Match values with AchInfoForm.tsx
const achDocumentTypeOptions = [
    { label: 'A voided check', value: 'VOID_CHECK' },
    {
        label: 'Letter or screenshot from bank stating account and routing number',
        value: 'BANK_LETTER',
    },
];

export type ChangeRequestModalProps = {
    isOpen: Boolean;
    toggle: () => void;
    payeeName: string;
    onSuccess?: () => void;
};

export const ChangeRequestModal = (props: ChangeRequestModalProps) => {
    const { isOpen, toggle, payeeName } = props;
    const [{ selectedPayee }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [{ campaign }] = useContext(CampaignContext);
    const [addressOptions, setAddressOptions] = useState<any[]>([]);
    const { vendorId } = useParams<{ vendorId: string }>();
    const {
        watch,
        control,
        reset,
        getValues,
        setValue,
        handleSubmit,
        setError,
        clearErrors,
        formState: { isDirty, dirtyFields, isSubmitting, errors },
    } = useForm();

    const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
    const [step, setStep] = useState<'form' | 'review'>('form');

    // Track original address values
    const [originalAddressValues, setOriginalAddressValues] = useState<any>({});
    const [addressFieldsChanged, setAddressFieldsChanged] = useState(false);
    const [isPrimaryAddress, setIsPrimaryAddress] = useState(false);

    const selected_address = watch('requested_address');
    const address_label = watch('address_label');
    const address_1 = watch('address_1');
    const address_2 = watch('address_2');
    const city = watch('city');
    const state = watch('state');
    const zip_code = watch('zip_code');
    const delete_address = watch('delete_address');
    const make_primary = watch('make_primary');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [achFile, setAchFile] = useState<File | null>(null);

    // Prepare address options for dropdown when component mounts or selectedPayee changes
    useEffect(() => {
        if (
            selectedPayee &&
            selectedPayee.vendor_addresses &&
            selectedPayee.vendor_addresses.length > 0
        ) {
            const options = selectedPayee.vendor_addresses.map(
                (address: any) => ({
                    value: address.id,
                    label: address.address_label || 'Address',
                    data: address,
                })
            );
            setAddressOptions(options);
        }
    }, [selectedPayee, isOpen]);

    // Set default form values from selectedPayee when modal opens
    useEffect(() => {
        if (isOpen && selectedPayee) {
            // Set default values for name, email, and phone
            setValue('name', selectedPayee.name || '');
            setValue('email', selectedPayee.email || '');
            setValue('phone', selectedPayee.phone || '');

            // Set payment_type if available
            if (selectedPayee.payment_type) {
                setValue('payment_type', selectedPayee.payment_type);
            }
        }
    }, [isOpen, selectedPayee, setValue]);

    // Update form when an address is selected
    useEffect(() => {
        if (selected_address) {
            const addressData = addressOptions.find(
                (option) => option.value === selected_address
            )?.data;
            if (addressData) {
                const addressValues = {
                    address_label: addressData.address_label || '',
                    address_1: addressData.address_1 || '',
                    address_2: addressData.address_2 || '',
                    city: addressData.city || '',
                    state: addressData.state?.state || '',
                    zip_code: addressData.zip_code || '',
                };

                // Store original values
                setOriginalAddressValues(addressValues);

                // Set form values
                setValue('address_label', addressValues.address_label);
                setValue('address_1', addressValues.address_1);
                setValue('address_2', addressValues.address_2);
                setValue('city', addressValues.city);
                setValue('state', addressValues.state);
                setValue('zip_code', addressValues.zip_code);

                // Reset change tracking
                setAddressFieldsChanged(false);

                // Reset checkbox values
                setValue('delete_address', false);
                setValue('make_primary', false);

                // Check if this is the primary address
                setIsPrimaryAddress(addressData.is_primary || false);

                // If the address has payment_type data, set it
                if (addressData.payment_type) {
                    setValue('payment_type', addressData.payment_type);
                }
            }
        }
    }, [selected_address, addressOptions, setValue]);

    // Check for changes in address fields
    useEffect(() => {
        if (selected_address && originalAddressValues) {
            const hasAddressChanged =
                address_label !== originalAddressValues.address_label ||
                address_1 !== originalAddressValues.address_1 ||
                address_2 !== originalAddressValues.address_2 ||
                city !== originalAddressValues.city ||
                state !== originalAddressValues.state ||
                zip_code !== originalAddressValues.zip_code ||
                delete_address ||
                make_primary;

            setAddressFieldsChanged(hasAddressChanged);
        }
    }, [
        address_label,
        address_1,
        address_2,
        city,
        state,
        zip_code,
        delete_address,
        make_primary,
        selected_address,
        originalAddressValues,
    ]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setAchFile(e.target.files[0]);
        }
    };

    const toggleSection = (section: string) => {
        setSelectedSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
                if (section === 'check_address') {
                    setValue('requested_address', '', { shouldDirty: false });
                    setValue('address_label', '', { shouldDirty: false });
                    setValue('address_1', '', { shouldDirty: false });
                    setValue('address_2', '', { shouldDirty: false });
                    setValue('city', '', { shouldDirty: false });
                    setValue('state', '', { shouldDirty: false });
                    setValue('zip_code', '', { shouldDirty: false });
                    setValue('delete_address', false, { shouldDirty: false });
                    setValue('make_primary', false, { shouldDirty: false });
                } else if (section === 'direct_deposit') {
                    setValue('account_type', '', { shouldDirty: false });
                    setValue('document_type', '', { shouldDirty: false });
                    setAchFile(null);
                }
            } else {
                next.add(section);
            }
            return next;
        });
    };

    const getToggleStyle = (active: boolean): React.CSSProperties => ({
        backgroundColor: active ? '#e8943a' : '#fdf0e6',
        borderColor: active ? '#e8943a' : '#fdf0e6',
        color: active ? '#000' : '#999',
        fontWeight: active ? 700 : 400,
    });

    const onSubmit = async (data: any) => {
        const {
            city,
            email,
            name,
            phone,
            state,
            address_1,
            address_2,
            zip_code,
            requested_address,
            account_type,
            document_type,
            delete_address,
            make_primary,
        } = data;

        const formdata: any = {
            name,
            email,
            phone,
            address_label: data.address_label,
            address_1,
            address_2,
            city,
            state,
            zip_code,
            vendor_address: requested_address,
            delete_address,
            make_primary,
        };

        const campaignId = campaign?.campaignDetail?.campaign_id;

        if (selectedSections.has('direct_deposit')) {
            try {
                // Map form values to ACH request values
                formdata.account_type = account_type;
                formdata.document_type = document_type;
                formdata.agreement_signed = true;

                // Upload ACH document if selected
                if (achFile && campaignId) {
                    // Normalize the filename to replace spaces and special characters
                    const normalizedFilename = normalizeFileName(achFile);
                    const { fields, url } =
                        await achDocumentPresignedUrlByCampaign(
                            campaignId,
                            normalizedFilename
                        );

                    // Resize the image if it's an image file, otherwise use original file
                    const fileType = achFile.type.split('/')[0];
                    const fileToUpload = fileType === 'image'
                        ? await resizeImage(achFile)
                        : achFile;

                    await uploadFile(fields, url, fileToUpload);
                    formdata.ach_document_url = `${url}ach/${campaignId}/${normalizedFilename}`;
                    formdata.ach_document_filename = normalizedFilename;
                }
            } catch (error) {
                console.error('Error uploading file:', error);
                toast.error('Error uploading file');
                return;
            }
        }

        try {
            await createchangerequest(formdata, vendorId);
            reset();
            setAchFile(null);
            setSelectedSections(new Set());
            setStep('form');
            toggle();
            toast.success('Change request submitted successfully');
            // Call the onSuccess callback if provided
            if (props.onSuccess) {
                props.onSuccess();
            }
        } catch (error) {
            toast.error('Error submitting change request');
        }
    };

    const isReviewDisabled = () => {
        if (isSubmitting) return true;

        const hasContactChanged = !!(
            dirtyFields.name || dirtyFields.email || dirtyFields.phone
        );

        if (selectedSections.has('check_address') && !selected_address) {
            return true;
        }

        if (selectedSections.has('direct_deposit')) {
            if (!watch('account_type') || !watch('document_type') || !achFile) {
                return true;
            }
        }

        const hasAddressChanges =
            selectedSections.has('check_address') &&
            selected_address &&
            addressFieldsChanged;
        const hasAchChanges =
            selectedSections.has('direct_deposit') &&
            !!(watch('account_type') && watch('document_type') && achFile);

        return !(hasContactChanged || hasAddressChanges || hasAchChanges);
    };

    const handleCancel = () => {
        reset();
        setSelectedSections(new Set());
        setStep('form');
        setAchFile(null);
        toggle();
    };

    const renderComparison = (oldVal: string, newVal: string) => {
        const changed = (oldVal || '') !== (newVal || '');
        return (
            <Row className='mb-2'>
                <Col xs={6}>
                    {changed ? (
                        <div
                            className='d-flex justify-content-between align-items-center fw-bold'
                            style={{
                                backgroundColor: '#fde8e8',
                                padding: '6px 10px',
                                borderRadius: '4px',
                            }}
                        >
                            <span>{oldVal || ''}</span>
                            <span style={{ color: '#dc3545' }}>✕</span>
                        </div>
                    ) : (
                        <div className='py-1'>{oldVal || ''}</div>
                    )}
                </Col>
                <Col xs={6}>
                    {changed ? (
                        <div
                            className='d-flex justify-content-between align-items-center fw-bold'
                            style={{
                                backgroundColor: '#e8f5e9',
                                padding: '6px 10px',
                                borderRadius: '4px',
                            }}
                        >
                            <span>{newVal || ''}</span>
                            <span style={{ color: '#4caf50' }}>✓</span>
                        </div>
                    ) : (
                        <div className='py-1'>{newVal || ''}</div>
                    )}
                </Col>
            </Row>
        );
    };

    const sectionHeaderStyle: React.CSSProperties = {
        letterSpacing: '1.5px',
        fontSize: '0.75rem',
    };

    const renderAddressBlock = (
        label: string,
        addr1: string,
        addr2: string,
        cityVal: string,
        stateVal: string,
        zipVal: string,
        variant: 'old' | 'new',
        hasChanges: boolean
    ) => {
        const bg =
            hasChanges
                ? variant === 'old'
                    ? '#fde8e8'
                    : '#e8f5e9'
                : 'transparent';
        const icon = variant === 'old' ? '✕' : '✓';
        const iconColor = variant === 'old' ? '#dc3545' : '#4caf50';
        const stateLabel =
            stateOptions.find((o) => o.value === stateVal)?.label || stateVal;

        return (
            <div
                style={{
                    backgroundColor: bg,
                    padding: '10px',
                    borderRadius: '4px',
                    borderLeft: '3px solid',
                    borderLeftColor: hasChanges ? iconColor : '#dee2e6',
                }}
            >
                <div className='d-flex justify-content-between'>
                    <div>
                        <div className='fw-bold'>{label}</div>
                        <div className='fw-bold'>{addr1}</div>
                        {addr2 && <div>{addr2}</div>}
                        <div>
                            {cityVal}, {stateLabel}
                        </div>
                        <div>{zipVal}</div>
                    </div>
                    {hasChanges && (
                        <span
                            style={{
                                color: iconColor,
                                fontWeight: 'bold',
                                alignSelf: 'start',
                            }}
                        >
                            {icon}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal
            centered
            isOpen={isOpen as boolean}
            toggle={handleCancel}
            size={step === 'review' ? 'lg' : undefined}
        >
            <ModalBody>
                {step === 'form' && (
                    <>
                        <h3>Change Request for {payeeName}</h3>
                        <p>
                            Please specify what you would like to change and we
                            will do our best to fulfill your request.
                        </p>

                        <FormGroup>
                            <TextInput
                                label='Name'
                                name='name'
                                type='text'
                                control={control}
                            />
                            <TextInput
                                label='Email Address'
                                name='email'
                                type='email'
                                control={control}
                            />
                            <TextInput
                                label='Phone Number'
                                name='phone'
                                type='tel'
                                control={control}
                            />
                        </FormGroup>

                        <FormGroup className='mb-4'>
                            <Label className='fw-bold'>Edit One or Both:</Label>
                            <div className='d-flex gap-2'>
                                <Button
                                    className='rounded-pill px-4'
                                    style={getToggleStyle(
                                        selectedSections.has('check_address')
                                    )}
                                    onClick={() =>
                                        toggleSection('check_address')
                                    }
                                >
                                    Check Address
                                </Button>
                                <Button
                                    className='rounded-pill px-4'
                                    style={getToggleStyle(
                                        selectedSections.has('direct_deposit')
                                    )}
                                    onClick={() =>
                                        toggleSection('direct_deposit')
                                    }
                                >
                                    Direct Deposit
                                </Button>
                            </div>
                        </FormGroup>

                        {selectedSections.has('check_address') && (
                            <div className='mb-4'>
                                <SelectInput
                                    control={control}
                                    name='requested_address'
                                    label='Requested Address'
                                    options={addressOptions}
                                    required={true}
                                />

                                {selected_address && (
                                    <div
                                        className='mt-3'
                                        style={{
                                            borderLeft: '1px solid #dee2e6',
                                            paddingLeft: '1rem',
                                            marginLeft: '0',
                                        }}
                                    >
                                        <TextInput
                                            label='Address Label'
                                            name='address_label'
                                            type='text'
                                            control={control}
                                        />
                                        <TextInput
                                            label='Street Address'
                                            name='address_1'
                                            type='text'
                                            control={control}
                                        />
                                        <TextInput
                                            label='Street Address 2'
                                            name='address_2'
                                            type='text'
                                            control={control}
                                        />
                                        <TextInput
                                            label='City'
                                            name='city'
                                            type='text'
                                            control={control}
                                        />
                                        <Row>
                                            <Col sm='8'>
                                                <SelectInput
                                                    control={control}
                                                    name='state'
                                                    label='State'
                                                    options={stateOptions}
                                                />
                                            </Col>
                                            <Col sm='4'>
                                                <TextInput
                                                    name='zip_code'
                                                    label='Zip Code'
                                                    type='zip'
                                                    control={control}
                                                />
                                            </Col>
                                        </Row>

                                        <div className='mt-3'>
                                            {!isPrimaryAddress && (
                                                <>
                                                    <CheckboxInput
                                                        id='delete_address'
                                                        name='delete_address'
                                                        label='Delete this address'
                                                        type='checkbox'
                                                        control={control}
                                                        value={true}
                                                        disabled={make_primary}
                                                    />
                                                    <CheckboxInput
                                                        id='make_primary'
                                                        name='make_primary'
                                                        label='Make this my primary check address'
                                                        type='checkbox'
                                                        control={control}
                                                        value={true}
                                                        disabled={
                                                            delete_address
                                                        }
                                                    />
                                                </>
                                            )}
                                            {isPrimaryAddress && (
                                                <div className='text-muted mt-1 small'>
                                                    <em>
                                                        This is already your
                                                        primary address and
                                                        cannot be deleted.
                                                    </em>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedSections.has('direct_deposit') && (
                            <div className='mb-4'>
                                <Label>
                                    Account Type{' '}
                                    <span className='required'>*</span>
                                </Label>
                                <div className='d-flex gap-3'>
                                    <CheckboxInput
                                        id='checking'
                                        name='account_type'
                                        label='Checking'
                                        type='radio'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        value={achAccountTypeOptions[0].value}
                                    />
                                    <CheckboxInput
                                        id='savings'
                                        name='account_type'
                                        label='Savings'
                                        type='radio'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        value={achAccountTypeOptions[1].value}
                                    />
                                </div>
                                <Label>
                                    I am providing one of the following:{' '}
                                    <span className='required'>*</span>
                                </Label>
                                <div>
                                    <CheckboxInput
                                        id='voided_check'
                                        name='document_type'
                                        label='A voided check'
                                        type='radio'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        value={achDocumentTypeOptions[0].value}
                                    />
                                    <CheckboxInput
                                        id='letter_screenshot'
                                        name='document_type'
                                        label='Letter or screenshot from bank stating account and routing number'
                                        type='radio'
                                        control={control}
                                        required={true}
                                        ariaRequired={true}
                                        value={achDocumentTypeOptions[1].value}
                                    />
                                </div>
                                <input
                                    type='file'
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                    accept='image/*,application/pdf'
                                />
                                <Button
                                    color='primary'
                                    size='sm'
                                    className='mb-3'
                                    onClick={() => {
                                        fileInputRef.current?.click();
                                    }}
                                >
                                    {achFile ? 'Change File' : 'Upload File'}
                                </Button>
                                {achFile && (
                                    <div className='mt-2 mb-2'>
                                        <span className='fw-bold'>
                                            Selected file:
                                        </span>{' '}
                                        {achFile.name}
                                        <Button
                                            color='link'
                                            size='sm'
                                            className='ms-2 p-0'
                                            onClick={() => setAchFile(null)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className='d-flex gap-2'>
                            <Button
                                color='outline-dark'
                                className='rounded-pill px-4'
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                color='primary'
                                className='rounded-pill px-4'
                                onClick={() => setStep('review')}
                                disabled={isReviewDisabled()}
                            >
                                Review
                            </Button>
                        </div>
                    </>
                )}

                {step === 'review' &&
                    (() => {
                        const vals = getValues();
                        const oldName = selectedPayee?.name || '';
                        const oldEmail = selectedPayee?.email || '';
                        const oldPhone = selectedPayee?.phone || '';

                        const selectedAddr = addressOptions.find(
                            (o) => o.value === selected_address
                        )?.data;

                        const oldAccountTypeLabel =
                            achAccountTypeOptions.find(
                                (o) => o.value === selectedPayee?.ach_type
                            )?.label || '';
                        const newAccountTypeLabel =
                            achAccountTypeOptions.find(
                                (o) => o.value === vals.account_type
                            )?.label || '';
                        const newDocTypeLabel =
                            achDocumentTypeOptions.find(
                                (o) => o.value === vals.document_type
                            )?.label || '';

                        const paymentLabel = (val: string) =>
                            paymentOptionsArray.find((o) => o.value === val)
                                ?.label || '';

                        return (
                            <>
                                <h3>Change Request {payeeName}</h3>
                                <p className='text-muted'>
                                    Please review your changes below and click
                                    &lsquo;Submit&rsquo; when the changes have
                                    been finalized.
                                </p>

                                <Row className='mb-3'>
                                    <Col xs={6}>
                                        <h5 className='fw-bold'>
                                            Old Payee Info
                                        </h5>
                                    </Col>
                                    <Col xs={6}>
                                        <h5 className='fw-bold'>
                                            Requested Payee Info
                                        </h5>
                                    </Col>
                                </Row>

                                <h6
                                    className='text-uppercase fw-bold mb-3'
                                    style={sectionHeaderStyle}
                                >
                                    Contact Information
                                </h6>
                                {renderComparison(oldName, vals.name || '')}
                                {renderComparison(oldEmail, vals.email || '')}
                                {renderComparison(oldPhone, vals.phone || '')}

                                {selectedSections.has('check_address') &&
                                    selectedAddr && (
                                        <>
                                            <h6
                                                className='text-uppercase fw-bold mb-3 mt-4'
                                                style={sectionHeaderStyle}
                                            >
                                                Check Address Information
                                            </h6>
                                            <Row className='mb-2'>
                                                <Col xs={6}>
                                                    {renderAddressBlock(
                                                        `${selectedAddr.address_label || 'Address'}${selectedAddr.is_primary ? ' (primary)' : ''}`,
                                                        selectedAddr.address_1 ||
                                                            '',
                                                        selectedAddr.address_2 ||
                                                            '',
                                                        selectedAddr.city || '',
                                                        selectedAddr.state
                                                            ?.state || '',
                                                        selectedAddr.zip_code ||
                                                            '',
                                                        'old',
                                                        addressFieldsChanged
                                                    )}
                                                </Col>
                                                <Col xs={6}>
                                                    {renderAddressBlock(
                                                        `${vals.address_label || 'Address'}${selectedAddr.is_primary || vals.make_primary ? ' (primary)' : ''}`,
                                                        vals.address_1 || '',
                                                        vals.address_2 || '',
                                                        vals.city || '',
                                                        vals.state || '',
                                                        vals.zip_code || '',
                                                        'new',
                                                        addressFieldsChanged
                                                    )}
                                                </Col>
                                            </Row>
                                            {vals.delete_address && (
                                                <div
                                                    className='text-danger small mt-1'
                                                    style={{
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    This address will be deleted
                                                </div>
                                            )}
                                        </>
                                    )}

                                {selectedSections.has('direct_deposit') && (
                                    <>
                                        <h6
                                            className='text-uppercase fw-bold mb-3 mt-4'
                                            style={sectionHeaderStyle}
                                        >
                                            Direct Deposit Information
                                        </h6>

                                        {renderComparison(
                                            oldAccountTypeLabel
                                                ? `Account Type: ${oldAccountTypeLabel}`
                                                : '',
                                            `Account Type: ${newAccountTypeLabel}`
                                        )}

                                        {selectedPayee?.ach_last_4 && (
                                            <Row className='mb-2'>
                                                <Col
                                                    xs={6}
                                                    className='py-1 text-muted fst-italic'
                                                >
                                                    Bank ····
                                                    {selectedPayee.ach_last_4}
                                                </Col>
                                                <Col xs={6}></Col>
                                            </Row>
                                        )}

                                        <Row className='mb-2'>
                                            <Col xs={6}></Col>
                                            <Col xs={6}>
                                                <div className='small text-muted mb-1'>
                                                    I am providing:
                                                </div>
                                                <div>{newDocTypeLabel}</div>
                                            </Col>
                                        </Row>

                                        {achFile && (
                                            <Row className='mb-2'>
                                                <Col xs={6}></Col>
                                                <Col xs={6}>
                                                    <span
                                                        className='badge rounded-pill px-3 py-2'
                                                        style={{
                                                            backgroundColor:
                                                                '#01aeb4',
                                                            color: '#fff',
                                                        }}
                                                    >
                                                        {achFile.name}
                                                    </span>
                                                </Col>
                                            </Row>
                                        )}

                                        {renderComparison(
                                            paymentLabel(
                                                selectedPayee?.payment_type ||
                                                    ''
                                            )
                                                ? `Preferred Payment Method: ${paymentLabel(selectedPayee?.payment_type || '')}`
                                                : '',
                                            `Preferred Payment Method: Direct Deposit (ACH)`
                                        )}
                                    </>
                                )}

                                <div className='d-flex gap-2 mt-4'>
                                    <Button
                                        color='outline-dark'
                                        className='rounded-pill px-4'
                                        onClick={() => setStep('form')}
                                    >
                                        BACK
                                    </Button>
                                    <Button
                                        color='warning'
                                        className='rounded-pill px-4 fw-bold'
                                        onClick={handleSubmit(onSubmit)}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting
                                            ? 'SUBMITTING...'
                                            : 'SUBMIT'}
                                    </Button>
                                </div>
                            </>
                        );
                    })()}
            </ModalBody>
        </Modal>
    );
};
